import { InfiniteScroll } from "@/components/InfiniteScroll";
import { TeacherCard } from "@/components/cards/TeacherCard";
import { Input } from "@/components/ui/input";
import { AdminPageLayout } from "@/layouts/AdminPageLayout";
import { getTeachers, getTeacherById, deleteTeacher } from "@/services/admin/teacher.service";
import { Teacher } from "@/services/admin/teacher.interfaces";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from "@/components/ui/dialog";

const Toolbar: React.FC<{ onSearch: () => void }> = ({ onSearch }) => (
  <section id="tools" className="mb-6">
    <div className="flex flex-wrap items-center justify-between gap-4">
      <div className="flex flex-1 items-center space-x-4">
        <div className="relative flex-1 max-w-xl">
          <Input
            type="search"
            placeholder="Search teachers..."
            className="pl-10"
            onChange={() => onSearch()}
          />
          <i className="fa-solid fa-search absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400"></i>
        </div>
      </div>
    </div>
  </section>
);

const TeacherManagementPage: React.FC = () => {
  const [resetKey, setResetKey] = useState(0);
  const navigate = useNavigate();
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
  const [teacherIdToDelete, setTeacherIdToDelete] = useState<string | null>(null);

  const handleEdit = async (teacherId: string) => {
    try {
      const teacher = await getTeacherById(teacherId);
      navigate(`/admin/access/teachers/${teacherId}`, { state: { teacher } });
    } catch (error) {
      console.error("Failed to fetch teacher:", error);
      toast.error("Failed to fetch teacher. Please try again.");
    }
  };

  const handleDelete = async (teacherId: string) => {
    setDeleteConfirmationOpen(true);
    setTeacherIdToDelete(teacherId);
  };

  const confirmDeleteTeacher = async () => {
    if (teacherIdToDelete) {
      try {
        await deleteTeacher(teacherIdToDelete);
        toast.success("Professor removido com sucesso.");
      } catch (error) {
        console.error("Falha ao remover professor:", error);
        toast.error("Falha ao remover professor. Por favor, tente novamente.");
      } finally {
        setDeleteConfirmationOpen(false);
        setTeacherIdToDelete(null);
      }
    }
  };

  const fetchTeachers = async (page: number): Promise<Teacher[]> => {
    try {
      const teachers = await getTeachers(page);
      return teachers;
    } catch (error) {
      console.error("Failed to fetch teachers:", error);
      toast.error("Failed to load teachers. Please try again.");
      return [];
    }
  };

  const renderItem = (teacher: Teacher, key: React.Key) => (
    <TeacherCard
      key={key}
      teacher={{
        ...teacher,
        role: "Teacher",
      }}
      teacherId={teacher.id}
      onEdit={() => handleEdit(teacher.id)}
      onDelete={() => handleDelete(teacher.id)}
      className="mb-4"
    />
  );

  const itemKey = (teacher: Teacher) => teacher.id;

  const handleSearch = () => {
    setResetKey((prev) => prev + 1);
  };

  return (
    <AdminPageLayout title="Gerenciamento de Professores">
      <main id="main" className="py-6">
        <Toolbar onSearch={handleSearch} />

        <InfiniteScroll<Teacher>
          fetchItems={fetchTeachers}
          renderItem={renderItem}
          itemKey={itemKey}
          layout="grid"
          gridCols={{ default: 2, lg: 3 }}
          key={resetKey}
        />
      </main>
      <Dialog open={deleteConfirmationOpen} onOpenChange={setDeleteConfirmationOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmar Remoção</DialogTitle>
            <DialogDescription>
              Tem certeza de que deseja remover este professor? Esta ação não pode ser desfeita.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose asChild>
              <button type="button" className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none bg-secondary text-secondary-foreground hover:bg-secondary/80 mr-2 px-4 py-2">
                Cancelar
              </button>
            </DialogClose>
            <button
              type="button"
              className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none bg-destructive text-destructive-foreground hover:bg-destructive/80 px-4 py-2"
              onClick={confirmDeleteTeacher}
            >
              Confirmar Remoção
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminPageLayout>
  );
};

export default TeacherManagementPage;
